import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { loadWellbeingDetails } from '../../../store/directusService';
import { updatePreSelects, updateSelectedItems } from '../../../utils/directusFunctions';
import { NO_CONTRACT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import { WELLBEING_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys } from '../../../utils/objectUtils';
import { hasRole, LOCAL_ADMIN, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import NotFound from '../../notFound/NotFound';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { validate } from './validateWellbeingAdmin';

import form from '../../../commonStyles/formStyles.module.css';

const acceptedRoles = [SUPERUSER];
const viewRoles = [LOCAL_ADMIN, SUPERUSER];
const _WELLBEING = 'wellbeing';
const _MESSAGE = 'Updating wellbeing collection';

const WellbeingAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [selectedWellbeingTypeIds, setSelectedWellbeingTypeIds] = useState([]);
    const [preSelectedWellbeingTypes, setPreSelectedWellbeingTypes] = useState([]);
    const [preSelectedWellbeingTypeIds, setPreSelectedWellbeingTypeIds] = useState([]);
    const [arrayWellbeingTypes, setArrayWellbeingTypes] = useState([]);
    const [keys, setKeys] = useState({ wellbeing: '0' });
    const [errors, setErrors] = useState({});
    const [isDisabled, setIsDisabled] = useState(true);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const wellbeingDetails = useSelector(
        (state) => state.entities.directusService.wellbeingDetails
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        if (contractId) contractSetup();
    }, [contractId]);

    useEffect(() => {
        setIsDisabled(!hasRole(acceptedRoles, roles));
    }, [roles]);

    useEffect(() => {
        if (wellbeingDetails?.length) {
            const { preSelects, preSelectIds } = updatePreSelects(
                _WELLBEING,
                wellbeingDetails,
                contractId
            );
            setPreSelectedWellbeingTypes(preSelects);
            setPreSelectedWellbeingTypeIds(preSelectIds);
            setArrayWellbeingTypes(wellbeingDetails);
        }
    }, [wellbeingDetails]);

    useEffect(() => {
        if(successMessage) return;
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage.includes(_MESSAGE)) contractSetup();
        if(!successMessage) setIsButtonDisabled(false);
    }, [successMessage]);

    // FNS
    const contractSetup = () => {
        setSelectedWellbeingTypeIds([]);
        setPreSelectedWellbeingTypes([]);
        setPreSelectedWellbeingTypeIds([]);
        setArrayWellbeingTypes([]);
        setKeys(clearKeys(keys));
        dispatch(loadWellbeingDetails());
        setErrors({});
    };

    const loadingError = () => {
        if (!contractId) return NO_CONTRACT_ERROR;
        if (wellbeingDetails?.length < 1) return WELLBEING_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    // EVENT HANDLERS
    const onWellbeingChange = (chosenIds) => {
        clearError('button');
        if (!chosenIds) chosenIds = [];
        setSelectedWellbeingTypeIds(chosenIds);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);

        const newErrors = validate(preSelectedWellbeingTypeIds, selectedWellbeingTypeIds);
        if (Object.keys(newErrors)?.length) {
            setErrors(newErrors);
            return;
        }

        updateSelectedItems(
            _WELLBEING,
            wellbeingDetails,
            selectedWellbeingTypeIds,
            contractId,
            _WELLBEING,
            dispatch
        );
    };

    // RENDER
    if (!hasRole(viewRoles, roles)) return <NotFound />;
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <form
            className={form.formWrapper}
            onSubmit={onSubmit}
            data-testid="wellbeing_admin_form_start">
            <MultiSelect
                id="wellbeing"
                key={keys.wellbeing}
                disabled={isDisabled}
                label="Wellbeing"
                placeholder="Select wellbeing types..."
                menuItems={arrayWellbeingTypes || []}
                preSelectedIds={preSelectedWellbeingTypeIds}
                preSelects={preSelectedWellbeingTypes}
                onChange={(chosenIds) => onWellbeingChange(chosenIds)}
            />

            <Button
                id="wellbeingAdminButton"
                content="UPDATE WELLBEING"
                disabled={isDisabled || isButtonDisabled}
                error={errors.button}
                clearError={() => clearError('button')}
            />
        </form>
    );
};

WellbeingAdmin.propTypes = {
    contractId: PropTypes.string
};

export default WellbeingAdmin;
